import { createSelector, createSlice } from "@reduxjs/toolkit";
import { getTickersAction, updateStockData } from "../../actions";
import Instrument from "../../entity/Instrument";
import { setSettings } from "./settings";
import { calculateMarketData } from '../../utils/marketData';

const initialState = {
  instruments: {},
  getInstruments: {
    ids: [],
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  selectedInstrumentId: undefined,
};

const instrumentSlice = createSlice({
  name: "instrument",
  initialState,
  reducers: {
    setSelectedInstrument: (state, { payload }) => {
      state.selectedInstrumentId = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getTickersAction.pending, (state, action) => {
      state.getInstruments.loading = true;
    });
    builder.addCase(getTickersAction.fulfilled, (state, { payload }) => {
      state.instruments = payload.data.instruments.reduce(
        (s, inst) => ({ ...s, [inst.instrumentId]: new Instrument(inst) }),
        {}
      );
      state.getInstruments.ids = payload.data.instruments.map(inst => inst.instrumentId);
      state.getInstruments.loading = false;
    });
    builder.addCase(getTickersAction.rejected, (state, action) => {
      state.fetchError = action.error;
      state.getInstruments.loading = false;
    });
    builder.addCase(setSettings.type, (state, { payload }) => {
      state.selectedInstrumentId =
        payload?.instruments?.find(ins => !!ins?.default)?.id || payload?.instruments[0]?.id;
    });
    builder.addCase(updateStockData.fulfilled, (state, { payload }) => {
      const fields = /** @type { const } */(['date', 'last', 'bid', 'bidSize', 'ask', 'askSize', 'high', 'low', 'open', 'volume', 'todayTurnover', 'vwap']);
      let updateData = {...state.instruments[payload.id]};
      fields.filter(fieldName => payload.data[fieldName] !== null && payload.data[fieldName] !== undefined).forEach(field => {
        switch (field) {
          case 'date':
            updateData['lastUpdatedDate'] = new Date(payload.data['date']).toISOString();
            break;
          default:
            updateData[field] = payload.data[field];
        }
      });
      // calculate changed fields
      updateData = calculateMarketData(updateData, { usePrevCloseDayForMcap: payload.data?.usePrevCloseDayForMcap });
      state.instruments[payload.id] = updateData;
    });
  },
});

export const instrumentSelector = createSelector(
  [
    state => state.instrument.getInstruments.ids.map(id => state.instrument.instruments[id]),
    state => state.instrument.getInstruments.loading,
    state => state.instrument.getInstruments.error,
    state => state.instrument.selectedInstrumentId
  ],
  (instruments, loading, error, selectedInstrumentId) => ({
    instruments,
    loading,
    error,
    selectedInstrumentId,
  })
);

export const selectedInstrumentSelector = createSelector(
  [state => state.instrument.selectedInstrumentId, state => state.instrument.instruments],
  (selectedInstrumentId, instruments) => new Instrument(instruments[selectedInstrumentId])
);

export const selectedInstrumentSettingSelector = createSelector(
  [state => state.instrument.selectedInstrumentId, state => state.companySettings.settings.instruments],
  (selectedInstrumentId, instrumentsSetting) => instrumentsSetting.find(instSetting => Number(instSetting.id) === Number(selectedInstrumentId))
);

export const instrumentIdSelector = state => state.instrument.selectedInstrumentId;

export const { setSelectedInstrument } = instrumentSlice.actions;

export default instrumentSlice;